import { RichTypography } from '@/components/atoms/RichTypography';
import { getFormattedDateWithName } from '@/utils/date';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface DefaultHTMLSwornDeclarationProps {
  firstName: string;
  lastName: string;
  restaurantName: string;
  address: string;
  suppliers: string[];
  date: Date | string;
  company: string;
  showSignedBy?: boolean;
}

export const DefaultHTMLSwornDeclaration = ({
  firstName,
  lastName,
  restaurantName,
  address,
  suppliers,
  date,
  company,
  showSignedBy = true
}: DefaultHTMLSwornDeclarationProps) => {
  const representative = `${firstName} ${lastName}`;
  const { t } = useTranslation();
  return (
    <div className="border rounded-xl w-full bg-orange-50 text-left">
      <div className="bg-primary p-7 w-full rounded-b-2xl rounded-t-xl">
        <img src="https://storage.googleapis.com/mon-resto-halal/image/logo-mrh.png" alt="Logo" className="w-2/5 mx-auto" />
      </div>
      <div className="px-5 py-12 text-gray-500">
        <span className="text-center text-black block mb-4">
          <h2 className="font-bold text-4xl">{t('swornDeclaration.title')}</h2>
          <h3 className="font-medium text-sm">{t('swornDeclaration.subtitle')}</h3>
        </span>
        <div className="mb-4 text-sm">
          {t('swornDeclaration.content.part1')}

          <b className={representative ? 'underline underline-offset-2 text-lg text-emerald-400' : 'text-primary'}>
            {representative || '...........................................'}
          </b>
          {t('swornDeclaration.content.representative')}

          <b className={restaurantName ? 'underline underline-offset-2 text-lg text-emerald-400' : 'text-primary'}>
            {restaurantName || '...........................................'}
          </b>
          {t('swornDeclaration.content.restaurant')}

          <b className={address ? 'underline underline-offset-2 text-lg text-emerald-400' : 'text-primary'}>
            {address || '...........................................'}
          </b>
          {t('swornDeclaration.content.address')}

          <b className={company ? 'underline underline-offset-2 text-lg text-emerald-400' : 'text-primary'}>
            {company || '...........................................'}
          </b>
          <RichTypography customColorClass="text-gray-500">{t('swornDeclaration.content.company')}</RichTypography>
        </div>
        <div className="mb-4 text-sm">{t('swornDeclaration.content.responsibility')}</div>
        <div className="mb-4 text-sm">
          <RichTypography customColorClass="text-gray-500">{t('swornDeclaration.content.suppliers')}</RichTypography>
          <ul className="pl-3">
            {suppliers.map((supplier, index) => (
              <li key={index} className="underline underline-offset-2 text-lg text-emerald-400">
                {index + 1}. <b>{supplier}</b>
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-4 text-sm">{t('swornDeclaration.content.authorization')}</div>

        {showSignedBy ? (
          <div className="text-center ">
            <div>
              <h3 className="text-xl"> {t('swornDeclaration.content.signedBy')}</h3>
              <div className=" items-center w-full py-3 bg-white rounded-full shadow-inner overflow-hidden text-center">
                <h1 className="bg-gradient-to-r text-3xl uppercase from-primary via-green-500 to-black inline-block text-transparent bg-clip-text">
                  {representative}
                </h1>
              </div>
            </div>

            <h3 className="text-xs mt-1">
              le <b>{getFormattedDateWithName(new Date(date))}</b>
            </h3>
          </div>
        ) : (
          <h3 className="text-lg">
            Le <b>{getFormattedDateWithName(new Date(date))}</b>
          </h3>
        )}

        <RichTypography variant="h5" marginClassName="mt-5" align="center" customColorClass="text-gray-500">
          {t('swornDeclaration.content.footer')}
        </RichTypography>
      </div>
    </div>
  );
};
